import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  FaArrowLeft,
  FaCalendarAlt,
  FaEdit,
  FaEnvelope,
  FaEye,
  FaPhone,
  FaShareAlt,
  FaTrash,
} from "react-icons/fa";
import axiosInstance from "../components/api/axiosInstance";
import { classifyPortalLead } from "./leadClassification";
import { mobileValidationMessage, sanitizeMobileInput } from "./portalPhone";
import { formatPhone } from "../utils/formatters";
import { canDeleteInPortal } from "./portalSession";
import PortalAlert from "./PortalAlert";
import PortalConfirmModal from "./PortalConfirmModal";
import PortalFormPage from "./PortalFormPage";
import PortalLeadFollowupModal from "./PortalLeadFollowupModal";
import { getLeadStatusTone } from "./PortalStatusSelect";
import PortalSelect from "./PortalSelect";
import { ResultsMobileCard, ResultsMobileList } from "../components/ResultsMobile";

function toDateInputValue(value) {
  if (!value) return "";
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
}

function toForm(lead) {
  return {
    Tenant_Client_Id: "",
    Project_Id: "",
    Service_Id: "",
    First_Name: lead?.First_Name || "",
    Last_Name: lead?.Last_Name || "",
    Country_Code: lead?.Country_Code || "+91",
    Mobile_Number: lead?.Mobile_Number || "",
    Alt_Phone: lead?.Alt_Phone || "",
    Email: lead?.Email || "",
    Property_Service: lead?.Property_Service || "",
    Property_Service_Link: lead?.Property_Service_Link || "",
    Tenant_Project_Name: lead?.Tenant_Project_Name || "",
    Property_Location: lead?.Property_Location || "",
    Requirement_Type: lead?.Requirement_Type || "",
    Sharing_Preference: lead?.Sharing_Preference || "",
    Move_In_Date: toDateInputValue(lead?.Move_In_Date),
    Property_Type_Id: lead?.Property_Type_Id ? String(lead.Property_Type_Id) : "",
    Budget_Min: lead?.Budget_Min ?? "",
    Budget_Max: lead?.Budget_Max ?? "",
    Preferred_Location: lead?.Preferred_Location || "",
    Lead_Status_Id: lead?.Lead_Status_Id ? String(lead.Lead_Status_Id) : "",
    Source_Id: lead?.Source_Id ? String(lead.Source_Id) : "",
    Site_Visit: !!lead?.Site_Visit,
    Notes: lead?.Notes || "",
  };
}

function buildPayload(form) {
  const countryCode = (() => {
    const raw = String(form.Country_Code || "+91").trim();
    if (!raw) return "+91";
    return raw.startsWith("+") ? raw : `+${raw}`;
  })();

  return {
    First_Name: form.First_Name.trim(),
    Last_Name: form.Last_Name.trim() || null,
    Country_Code: countryCode,
    Mobile_Number: form.Mobile_Number.trim(),
    Alt_Phone: form.Alt_Phone.trim() || null,
    Email: form.Email.trim() || null,
    Property_Service: form.Property_Service.trim() || null,
    Property_Service_Link: form.Property_Service_Link.trim() || null,
    Tenant_Project_Name: form.Tenant_Project_Name.trim() || null,
    Property_Location: form.Property_Location.trim() || null,
    Requirement_Type: form.Requirement_Type || null,
    Sharing_Preference: form.Project_Id ? form.Sharing_Preference.trim() || null : null,
    Move_In_Date: form.Project_Id ? form.Move_In_Date || null : null,
    Property_Type_Id: form.Property_Type_Id || null,
    Budget_Min: form.Budget_Min !== "" ? form.Budget_Min : null,
    Budget_Max: form.Budget_Max !== "" ? form.Budget_Max : null,
    Preferred_Location: form.Preferred_Location.trim() || null,
    Lead_Status_Id: form.Lead_Status_Id ? Number(form.Lead_Status_Id) : null,
    Source_Id: form.Source_Id ? Number(form.Source_Id) : null,
    Site_Visit: !!form.Site_Visit,
    Notes: form.Notes.trim() || null,
  };
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function displayValue(value) {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

function normalizedCountryCode(lead) {
  const raw = String(lead?.Country_Code || "").trim();
  if (!raw) return "";
  return raw.startsWith("+") ? raw : `+${raw}`;
}

function fullName(lead) {
  return `${(lead?.First_Name || "").trim()} ${(lead?.Last_Name || "").trim()}`.trim() || "—";
}

function phone(lead) {
  if (!lead?.Mobile_Number) return "—";
  const digits = `${lead.Country_Code || ""}${lead.Mobile_Number}`.replace(/\D/g, "");
  return formatPhone(digits) || "—";
}

function telHref(lead) {
  const raw = `${normalizedCountryCode(lead)}${lead?.Mobile_Number || ""}`.replace(/[^\d+]/g, "");
  if (!raw) return null;
  return `tel:${raw.startsWith("+") ? raw : `+${raw}`}`;
}

function altTelHref(lead) {
  const raw = String(lead?.Alt_Phone || "").replace(/[^\d+]/g, "");
  if (!raw) return null;
  return `tel:${raw.startsWith("+") ? raw : `+${raw}`}`;
}

function budgetRange(lead) {
  if (!lead?.Budget_Min && !lead?.Budget_Max) return "—";
  return `${lead.Budget_Min || "—"} – ${lead.Budget_Max || "—"}`;
}

function backLabelForPath(path) {
  if (path === "/portal/customers") return "Back to customers";
  if (path === "/portal/clients") return "Back to clients";
  return "Back to leads";
}

function initials(lead) {
  const first = (lead?.First_Name || "").trim().charAt(0);
  const last = (lead?.Last_Name || "").trim().charAt(0);
  return `${first}${last}`.toUpperCase() || "L";
}

function statusBadgeClass(status) {
  const tone = getLeadStatusTone(status);
  if (tone === "neutral") return "cportal-badge";
  return `cportal-badge cportal-badge--${tone}`;
}

function sharePropertyLink(lead) {
  const url = String(lead?.Property_Service_Link || "").trim();
  if (!url) return;
  const title = String(lead?.Property_Service || "").trim() || "Property link";

  (async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title, url });
        return;
      }
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        alert("Property link copied to clipboard.");
        return;
      }
      window.prompt("Copy this link:", url);
    } catch (_) {
      /* user cancelled */
    }
  })();
}

function backPathForMode(mode) {
  if (mode === "customers") return "/portal/customers";
  if (mode === "clients") return "/portal/clients";
  return "/portal/leads";
}

function DetailItem({ label, value, children }) {
  return (
    <div className="cportal-profile-view-item">
      <span className="cportal-profile-view-label">{label}</span>
      <span className="cportal-profile-view-value">{children ?? displayValue(value)}</span>
    </div>
  );
}

function TenantLeadView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [requirementTypes, setRequirementTypes] = useState(["Buy", "Rent", "Lease", "Accommodation"]);
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [sources, setSources] = useState([]);
  const [tenantClients, setTenantClients] = useState([]);
  const [clientProjects, setClientProjects] = useState([]);
  const [clientServices, setClientServices] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [countries, setCountries] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [followupModalOpen, setFollowupModalOpen] = useState(false);
  const [editingFollowup, setEditingFollowup] = useState(null);
  const [detailTab, setDetailTab] = useState("overview");
  const canDelete = useMemo(() => canDeleteInPortal(), []);

  const useProjectMode = !loadingAssets && clientProjects.length > 0;
  const useServiceMode = !loadingAssets && clientProjects.length === 0 && clientServices.length > 0;

  const phoneCountryOptions = useMemo(() => {
    const mapped = (countries || [])
      .map((c) => ({
        code: String(c.country_code || c.country_short_code || c.Country_Code || "").trim(),
      }))
      .filter((x) => x.code);
    const codes = new Set(["+91"]);
    mapped.forEach((c) => {
      const code = c.code.startsWith("+") ? c.code : `+${c.code}`;
      codes.add(code);
    });
    return Array.from(codes).sort((a, b) => a.localeCompare(b));
  }, [countries]);

  const backPath = useMemo(() => {
    if (location.state?.from) return backPathForMode(location.state.from);
    if (lead) return backPathForMode(classifyPortalLead(lead));
    return "/portal/leads";
  }, [location.state, lead]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axiosInstance.get(`/client/leads/${id}`);
        if (!active) return;
        setLead(res.data?.data || null);
        setForm(toForm(res.data?.data));
        if (!res.data?.data) setError("Lead not found.");
      } catch (err) {
        if (!active) return;
        setError(err.response?.data?.message || "Could not load lead details.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [id]);

  useEffect(() => {
    if (location.state?.edit) {
      setEditing(true);
    }
  }, [location.state?.edit]);

  useEffect(() => {
    if (!editing) return;
    let active = true;
    (async () => {
      setLoadingClients(true);
      try {
        const [metaRes, countriesRes, clientsRes] = await Promise.all([
          axiosInstance.get("/client/lead-meta"),
          axiosInstance.get("/client/countries").catch(() => ({ data: { data: [] } })),
          axiosInstance.get("/client/tenant-clients").catch(() => ({ data: { data: [] } })),
        ]);
        if (!active) return;
        const data = metaRes.data?.data || {};
        setPropertyTypes(Array.isArray(data.propertyTypes) ? data.propertyTypes : []);
        if (Array.isArray(data.requirementTypes) && data.requirementTypes.length) {
          setRequirementTypes(data.requirementTypes);
        }
        setLeadStatuses(Array.isArray(data.leadStatuses) ? data.leadStatuses : []);
        setSources(Array.isArray(data.sources) ? data.sources : []);
        const rawCountries = Array.isArray(countriesRes?.data?.data)
          ? countriesRes.data.data
          : Array.isArray(countriesRes?.data)
            ? countriesRes.data
            : [];
        setCountries(rawCountries);
        const rawClients = Array.isArray(clientsRes.data?.data)
          ? clientsRes.data.data
          : Array.isArray(clientsRes.data)
            ? clientsRes.data
            : [];
        setTenantClients(
          rawClients
            .filter((c) => String(c.Status || "Active").toLowerCase() !== "inactive")
            .map((c) => ({
              id: c.Tenant_Client_Id,
              name: c.Client_Name || `Client #${c.Tenant_Client_Id}`,
            }))
            .filter((c) => c.id != null)
        );
      } catch (_) {
        // Meta is optional; form still works with defaults.
      } finally {
        if (active) setLoadingClients(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [editing]);

  useEffect(() => {
    if (!editing) return;
    let active = true;
    const clientId = form.Tenant_Client_Id;
    if (!clientId) {
      setClientProjects([]);
      setClientServices([]);
      return undefined;
    }
    setLoadingAssets(true);
    (async () => {
      try {
        const [projectsRes, servicesRes] = await Promise.all([
          axiosInstance.get(`/client/tenant-clients/${clientId}/projects`),
          axiosInstance.get(`/client/tenant-clients/${clientId}/services`),
        ]);
        if (!active) return;
        const projectsRaw = Array.isArray(projectsRes.data?.data) ? projectsRes.data.data : [];
        const servicesRaw = Array.isArray(servicesRes.data?.data) ? servicesRes.data.data : [];
        const projects = projectsRaw
          .filter((p) => String(p.Status || "Active").toLowerCase() !== "inactive")
          .map((p) => ({
            id: p.Tenant_Project_Id,
            name: p.Project_Name || `Project #${p.Tenant_Project_Id}`,
            label: [p.Short_Code, p.Project_Name].filter(Boolean).join(" — ") || p.Project_Name,
          }))
          .filter((p) => p.id != null);
        const services = servicesRaw
          .filter((s) => String(s.Status || "Active").toLowerCase() !== "inactive")
          .map((s) => ({
            id: s.Tenant_Client_Service_Id,
            name: s.Service_Name || `Service #${s.Tenant_Client_Service_Id}`,
          }))
          .filter((s) => s.id != null);
        setClientProjects(projects);
        setClientServices(services);

        setForm((prev) => {
          if (prev.Project_Id || prev.Service_Id) return prev;
          const projectName = String(prev.Tenant_Project_Name || "").trim().toLowerCase();
          const serviceName = String(prev.Property_Service || "").trim().toLowerCase();
          if (projects.length && projectName) {
            const match = projects.find((p) => String(p.name).trim().toLowerCase() === projectName);
            if (match) {
              return {
                ...prev,
                Project_Id: String(match.id),
                Service_Id: "",
                Tenant_Project_Name: match.name,
                Property_Service: match.name,
              };
            }
          }
          if (!projects.length && services.length && serviceName) {
            const match = services.find((s) => String(s.name).trim().toLowerCase() === serviceName);
            if (match) {
              return {
                ...prev,
                Service_Id: String(match.id),
                Project_Id: "",
                Tenant_Project_Name: "",
                Property_Service: match.name,
              };
            }
          }
          return prev;
        });
      } catch (_) {
        if (active) {
          setClientProjects([]);
          setClientServices([]);
        }
      } finally {
        if (active) setLoadingAssets(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [editing, form.Tenant_Client_Id]);

  useEffect(() => {
    if (!editing || form.Tenant_Client_Id || !tenantClients.length) return;
    let active = true;
    const wantProject = String(form.Tenant_Project_Name || "").trim().toLowerCase();
    const wantService = String(form.Property_Service || "").trim().toLowerCase();
    if (!wantProject && !wantService) return undefined;

    (async () => {
      for (const client of tenantClients) {
        if (!active) return;
        try {
          const [projectsRes, servicesRes] = await Promise.all([
            axiosInstance.get(`/client/tenant-clients/${client.id}/projects`),
            axiosInstance.get(`/client/tenant-clients/${client.id}/services`),
          ]);
          const projects = Array.isArray(projectsRes.data?.data) ? projectsRes.data.data : [];
          const services = Array.isArray(servicesRes.data?.data) ? servicesRes.data.data : [];
          const projectMatch =
            wantProject &&
            projects.find((p) => String(p.Project_Name || "").trim().toLowerCase() === wantProject);
          const serviceMatch =
            !wantProject &&
            wantService &&
            services.find((s) => String(s.Service_Name || "").trim().toLowerCase() === wantService);
          if (projectMatch || serviceMatch) {
            if (!active) return;
            setForm((prev) => ({ ...prev, Tenant_Client_Id: String(client.id) }));
            return;
          }
        } catch (_) {
          // continue
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [editing, tenantClients, form.Tenant_Client_Id, form.Tenant_Project_Name, form.Property_Service]);

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const handleSelectClient = (clientId) => {
    setForm((f) => ({
      ...f,
      Tenant_Client_Id: clientId,
      Project_Id: "",
      Service_Id: "",
      Property_Service: "",
      Tenant_Project_Name: "",
      Sharing_Preference: "",
      Move_In_Date: "",
    }));
  };

  const handleSelectProject = (projectId) => {
    const project = clientProjects.find((p) => String(p.id) === String(projectId));
    const name = project?.name || "";
    setForm((f) => ({
      ...f,
      Project_Id: projectId,
      Service_Id: "",
      Tenant_Project_Name: name,
      Property_Service: name,
      ...(projectId ? {} : { Sharing_Preference: "", Move_In_Date: "" }),
    }));
  };

  const handleSelectService = (serviceId) => {
    const service = clientServices.find((s) => String(s.id) === String(serviceId));
    const name = service?.name || "";
    setForm((f) => ({
      ...f,
      Service_Id: serviceId,
      Project_Id: "",
      Tenant_Project_Name: "",
      Property_Service: name,
      Sharing_Preference: "",
      Move_In_Date: "",
    }));
  };

  const closeFollowupModal = () => {
    setFollowupModalOpen(false);
    setEditingFollowup(null);
  };

  const openAddFollowupModal = () => {
    setEditingFollowup(null);
    setFollowupModalOpen(true);
  };

  const openEditFollowupModal = (followup) => {
    setEditingFollowup(followup);
    setFollowupModalOpen(true);
  };

  const reloadLead = async () => {
    const res = await axiosInstance.get(`/client/leads/${id}`);
    const data = res.data?.data || null;
    setLead(data);
    if (!editing) setForm(toForm(data));
    return data;
  };

  const handleDelete = () => {
    if (!canDelete || !lead) return;
    setConfirmDelete(true);
  };

  const closeDeleteConfirm = () => {
    if (deleting) return;
    setConfirmDelete(false);
  };

  const confirmDeleteLead = async () => {
    if (!canDelete || !lead) return;
    setDeleting(true);
    setMessage({ type: "", text: "" });
    try {
      await axiosInstance.delete(`/client/leads/${id}`);
      navigate(backPath, { replace: true });
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Could not delete lead.",
      });
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.Tenant_Client_Id) {
      setMessage({ type: "error", text: "Client is required." });
      return;
    }
    if (useProjectMode && !form.Project_Id) {
      setMessage({ type: "error", text: "Project is required." });
      return;
    }
    if (useServiceMode && !form.Service_Id) {
      setMessage({ type: "error", text: "Service is required." });
      return;
    }
    if (form.Tenant_Client_Id && !loadingAssets && !clientProjects.length && !clientServices.length) {
      setMessage({ type: "error", text: "Selected client has no projects or services." });
      return;
    }
    if (!form.First_Name.trim()) {
      setMessage({ type: "error", text: "First name is required." });
      return;
    }
    const mobileError = mobileValidationMessage(form.Mobile_Number, { required: true });
    if (mobileError) {
      setMessage({ type: "error", text: mobileError });
      return;
    }
    const altPhoneError = mobileValidationMessage(form.Alt_Phone, { label: "Alternate phone" });
    if (altPhoneError) {
      setMessage({ type: "error", text: altPhoneError });
      return;
    }

    setSaving(true);
    setMessage({ type: "", text: "" });
    try {
      const res = await axiosInstance.put(`/client/leads/${id}`, buildPayload(form));
      const data = res.data?.data;
      if (data) {
        setLead(data);
        setForm(toForm(data));
      }
      setEditing(false);
      setMessage({ type: "success", text: "Lead updated." });
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Could not update lead.",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="cportal-page">
        <p className="cportal-muted">Loading…</p>
      </div>
    );
  }

  if (error || !lead) {
    return (
      <div className="cportal-page">
        <button type="button" className="cportal-back-link" onClick={() => navigate(backPath)}>
          <FaArrowLeft /> Back
        </button>
        <div className="cportal-alert cportal-alert-error">{error || "Lead not found."}</div>
      </div>
    );
  }

  if (editing) {
    const cancelEdit = () => {
      setEditing(false);
      setForm(toForm(lead));
      setMessage({ type: "", text: "" });
    };
    return (
      <PortalFormPage
        title="Edit Lead"
        backLabel="Back to lead"
        onBack={cancelEdit}
        onSubmit={handleSave}
        submitting={saving}
        submitLabel="Save changes"
        className="cportal-lead-form"
      >
          {message.text && (
            <PortalAlert
              type={message.type === "error" ? "error" : "success"}
              message={message.text}
              onClear={() => setMessage({ type: "", text: "" })}
            />
          )}

          <h3 className="cportal-form-section">Lead Contact</h3>
          <div className="cportal-form-grid">
            <div className="cportal-field">
              <label>First name <span className="cportal-required">*</span></label>
              <input value={form.First_Name} onChange={(e) => setField("First_Name", e.target.value)} required />
            </div>
            <div className="cportal-field">
              <label>Last name</label>
              <input value={form.Last_Name} onChange={(e) => setField("Last_Name", e.target.value)} />
            </div>
            <div className="cportal-field cportal-field-phone">
              <label>Mobile number <span className="cportal-required">*</span></label>
              <div className="cportal-phone-row">
                <PortalSelect
                  value={form.Country_Code}
                  onChange={(e) => setField("Country_Code", e.target.value)}
                  aria-label="Country code"
                >
                  {phoneCountryOptions.map((code) => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
                </PortalSelect>
                <input
                  value={form.Mobile_Number}
                  onChange={(e) =>
                    setField("Mobile_Number", sanitizeMobileInput(e.target.value))
                  }
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="10-digit number"
                  required
                />
              </div>
            </div>
            <div className="cportal-field">
              <label>Alternate phone</label>
              <input
                value={form.Alt_Phone}
                onChange={(e) => setField("Alt_Phone", sanitizeMobileInput(e.target.value))}
                inputMode="numeric"
                maxLength={10}
                placeholder="10-digit number"
              />
            </div>
            <div className="cportal-field">
              <label>Email</label>
              <input type="email" value={form.Email} onChange={(e) => setField("Email", e.target.value)} />
            </div>
            <div className="cportal-field">
              <label>Lead Location</label>
              <input
                value={form.Preferred_Location}
                onChange={(e) => setField("Preferred_Location", e.target.value)}
              />
            </div>
          </div>

          <h3 className="cportal-form-section">Client Assignment</h3>
          <div className="cportal-form-grid">
            <div className="cportal-field">
              <label>
                Client <span className="cportal-required">*</span>
              </label>
              <PortalSelect
                value={form.Tenant_Client_Id}
                onChange={(e) => handleSelectClient(e.target.value)}
                disabled={loadingClients}
                required
              >
                <option value="">
                  {loadingClients
                    ? "Loading clients…"
                    : tenantClients.length
                      ? "Select client…"
                      : "No clients available"}
                </option>
                {tenantClients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </PortalSelect>
            </div>

            {form.Tenant_Client_Id ? (
              loadingAssets ? (
                <div className="cportal-field">
                  <label>Project / Service</label>
                  <PortalSelect disabled value="">
                    <option value="">Loading…</option>
                  </PortalSelect>
                </div>
              ) : useProjectMode ? (
                <div className="cportal-field">
                  <label>
                    Project <span className="cportal-required">*</span>
                  </label>
                  <PortalSelect
                    value={form.Project_Id}
                    onChange={(e) => handleSelectProject(e.target.value)}
                    required
                  >
                    <option value="">Select project…</option>
                    {clientProjects.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.label || p.name}
                      </option>
                    ))}
                  </PortalSelect>
                </div>
              ) : useServiceMode ? (
                <div className="cportal-field">
                  <label>
                    Service <span className="cportal-required">*</span>
                  </label>
                  <PortalSelect
                    value={form.Service_Id}
                    onChange={(e) => handleSelectService(e.target.value)}
                    required
                  >
                    <option value="">Select service…</option>
                    {clientServices.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </PortalSelect>
                </div>
              ) : (
                <div className="cportal-field">
                  <label>Project / Service</label>
                  <PortalSelect disabled value="">
                    <option value="">No projects or services for this client</option>
                  </PortalSelect>
                </div>
              )
            ) : (
              <div className="cportal-field">
                <label>Project / Service</label>
                <PortalSelect disabled value="">
                  <option value="">Select client first</option>
                </PortalSelect>
              </div>
            )}
          </div>

          <h3 className="cportal-form-section">Property Requirements</h3>
          <div className="cportal-form-grid">
            <div className="cportal-field">
              <label>Property link</label>
              <input
                value={form.Property_Service_Link}
                onChange={(e) => setField("Property_Service_Link", e.target.value)}
              />
            </div>
            <div className="cportal-field">
              <label>Property location</label>
              <input
                value={form.Property_Location}
                onChange={(e) => setField("Property_Location", e.target.value)}
              />
            </div>
            <div className="cportal-field">
              <label>Requirement type</label>
              <PortalSelect
                value={form.Requirement_Type}
                onChange={(e) => setField("Requirement_Type", e.target.value)}
              >
                <option value="">Select…</option>
                {requirementTypes.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </PortalSelect>
            </div>
            {form.Project_Id ? (
              <>
                <div className="cportal-field">
                  <label>Sharing preference</label>
                  <input
                    type="text"
                    value={form.Sharing_Preference}
                    onChange={(e) => setField("Sharing_Preference", e.target.value)}
                    placeholder="Enter sharing preference"
                    maxLength={100}
                  />
                </div>
                <div className="cportal-field">
                  <label>Move-in date</label>
                  <input
                    type="date"
                    value={form.Move_In_Date}
                    onChange={(e) => setField("Move_In_Date", e.target.value)}
                  />
                </div>
              </>
            ) : null}
            <div className="cportal-field">
              <label>Property type</label>
              <PortalSelect
                value={form.Property_Type_Id}
                onChange={(e) => setField("Property_Type_Id", e.target.value)}
              >
                <option value="">Select…</option>
                {propertyTypes.map((p) => (
                  <option key={p.Property_Type_Id} value={p.Property_Type_Id}>
                    {p.Property_Type_Name}
                  </option>
                ))}
              </PortalSelect>
            </div>
            <div className="cportal-field">
              <label>Budget min</label>
              <input
                type="number"
                value={form.Budget_Min}
                onChange={(e) => setField("Budget_Min", e.target.value)}
              />
            </div>
            <div className="cportal-field">
              <label>Budget max</label>
              <input
                type="number"
                value={form.Budget_Max}
                onChange={(e) => setField("Budget_Max", e.target.value)}
              />
            </div>
          </div>

          <h3 className="cportal-form-section">Lead Pipeline</h3>
          <div className="cportal-form-grid">
            <div className="cportal-field">
              <label>
                Status <span className="cportal-required">*</span>
              </label>
              <PortalSelect
                value={form.Lead_Status_Id}
                onChange={(e) => setField("Lead_Status_Id", e.target.value)}
              >
                <option value="">Select…</option>
                {leadStatuses.map((s) => (
                  <option key={s.Status_Id} value={s.Status_Id}>
                    {s.Status}
                  </option>
                ))}
              </PortalSelect>
            </div>
            <div className="cportal-field">
              <label>Source</label>
              <PortalSelect
                value={form.Source_Id}
                onChange={(e) => setField("Source_Id", e.target.value)}
              >
                <option value="">Select…</option>
                {sources.map((s) => (
                  <option key={s.Source_Id} value={s.Source_Id}>
                    {s.Source_Name}
                  </option>
                ))}
              </PortalSelect>
            </div>
            <div className="cportal-field cportal-field-check">
              <label>
                <input
                  type="checkbox"
                  checked={form.Site_Visit}
                  onChange={(e) => setField("Site_Visit", e.target.checked)}
                />
                <span>Site visit</span>
              </label>
            </div>
          </div>

          <h3 className="cportal-form-section">Notes</h3>
          <div className="cportal-field">
            <textarea rows={3} value={form.Notes} onChange={(e) => setField("Notes", e.target.value)} />
          </div>
      </PortalFormPage>
    );
  }

  const followups = Array.isArray(lead.followups) ? lead.followups : [];
  const clientLabel =
    lead.Client_Name ||
    lead.Tenant_Client_Name ||
    lead.Customer_Name ||
    null;

  return (
    <div className="cportal-page cportal-lead-page">
      <button type="button" className="cportal-back-link" onClick={() => navigate(backPath)}>
        <FaArrowLeft /> {backLabelForPath(backPath)}
      </button>

      {message.text && (
        <PortalAlert
          type={message.type === "error" ? "error" : "success"}
          message={message.text}
          onClear={() => setMessage({ type: "", text: "" })}
        />
      )}

      <section className="cportal-lead-crm-summary">
        <div className="cportal-lead-crm-avatar" aria-hidden="true">
          {initials(lead)}
        </div>
        <div className="cportal-lead-crm-summary-main">
          <div className="cportal-lead-crm-name-row">
            <h1>{fullName(lead)}</h1>
            <span className={statusBadgeClass(lead.Lead_Status)}>
              {lead.Lead_Status || "No status"}
            </span>
          </div>
          <div className="cportal-lead-crm-contact-strip">
            {lead.Mobile_Number && telHref(lead) ? (
              <a href={telHref(lead)}>
                <FaPhone style={{ transform: "scaleX(-1)" }} /> {phone(lead)}
              </a>
            ) : null}
            {lead.Email ? (
              <a href={`mailto:${lead.Email}`}>
                <FaEnvelope /> {lead.Email}
              </a>
            ) : null}
            {lead.Preferred_Location ? <span>{lead.Preferred_Location}</span> : null}
          </div>
        </div>
        <div className="cportal-lead-crm-summary-actions">
          <button
            type="button"
            className="cportal-btn cportal-btn-edit"
            onClick={() => setEditing(true)}
          >
            <FaEdit aria-hidden="true" /> <span className="cportal-btn-text">Edit</span>
          </button>
          {canDelete && (
            <button
              type="button"
              className="cportal-btn cportal-btn-danger-outline"
              onClick={handleDelete}
              disabled={deleting}
            >
              <FaTrash aria-hidden="true" />
              {deleting ? "Deleting…" : "Delete"}
            </button>
          )}
        </div>
      </section>

      <div className="cportal-lead-crm-layout">
        <select
          className="cportal-section-select"
          value={detailTab}
          onChange={(e) => setDetailTab(e.target.value)}
          aria-label="Lead section"
        >
          <option value="overview">Overview</option>
          <option value="followups">Followups ({followups.length})</option>
        </select>
        <div className="cportal-lead-crm-tabs" role="tablist" aria-label="Lead details">
          <button
            type="button"
            role="tab"
            aria-selected={detailTab === "overview"}
            className={detailTab === "overview" ? "active" : ""}
            onClick={() => setDetailTab("overview")}
          >
            Overview
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={detailTab === "followups"}
            className={detailTab === "followups" ? "active" : ""}
            onClick={() => setDetailTab("followups")}
          >
            Followups
            <span className="cportal-lead-crm-tab-count">{followups.length}</span>
          </button>
        </div>

        {detailTab === "overview" ? (
          <div className="cportal-lead-crm-overview">
            <section className="cportal-lead-crm-card">
              <h3>Lead Contact</h3>
              <div className="cportal-lead-crm-grid">
                <DetailItem label="Name" value={fullName(lead)} />
                <DetailItem label="Email" value={lead.Email}>
                  {lead.Email ? (
                    <a href={`mailto:${lead.Email}`} className="cportal-contact-link">
                      <FaEnvelope size={12} />
                      {lead.Email}
                    </a>
                  ) : (
                    "—"
                  )}
                </DetailItem>
                <DetailItem label="Mobile" value={phone(lead)}>
                  {lead.Mobile_Number && telHref(lead) ? (
                    <a href={telHref(lead)} className="cportal-contact-link">
                      <FaPhone size={12} style={{ transform: "scaleX(-1)" }} />
                      {phone(lead)}
                    </a>
                  ) : (
                    displayValue(phone(lead))
                  )}
                </DetailItem>
                <DetailItem label="Alternate phone" value={lead.Alt_Phone}>
                  {lead.Alt_Phone && altTelHref(lead) ? (
                    <a href={altTelHref(lead)} className="cportal-contact-link">
                      {lead.Alt_Phone}
                    </a>
                  ) : (
                    displayValue(lead.Alt_Phone)
                  )}
                </DetailItem>
                <DetailItem label="Lead Location" value={lead.Preferred_Location} />
              </div>
            </section>

            {(clientLabel || lead.Tenant_Project_Name || lead.Property_Service) && (
              <section className="cportal-lead-crm-card">
                <h3>Client &amp; Assignment</h3>
                <div className="cportal-lead-crm-grid">
                  <DetailItem label="Client" value={clientLabel} />
                  <DetailItem
                    label="Project / Service"
                    value={lead.Tenant_Project_Name || lead.Property_Service}
                  />
                </div>
              </section>
            )}

            <section className="cportal-lead-crm-card">
              <h3>Property Requirements</h3>
              <div className="cportal-lead-crm-grid">
                <DetailItem label="Service" value={lead.Property_Service}>
                  <div className="cportal-property-service-row">
                    <span>{displayValue(lead.Property_Service)}</span>
                    {lead.Property_Service_Link && (
                      <span className="cportal-property-link-actions">
                        <a
                          href={lead.Property_Service_Link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="cportal-property-link-btn cportal-property-link-btn--view"
                        >
                          <FaEye size={12} />
                          View
                        </a>
                        <button
                          type="button"
                          className="cportal-property-link-btn cportal-property-link-btn--share"
                          onClick={() => sharePropertyLink(lead)}
                        >
                          <FaShareAlt size={12} />
                          Share
                        </button>
                      </span>
                    )}
                  </div>
                </DetailItem>
                <DetailItem label="Project" value={lead.Tenant_Project_Name} />
                <DetailItem label="Property location" value={lead.Property_Location} />
                <DetailItem label="Property type" value={lead.Property_Type_Name} />
                <DetailItem label="Requirement type" value={lead.Requirement_Type} />
                <DetailItem label="Sharing preference" value={lead.Sharing_Preference} />
                <DetailItem
                  label="Move-in date"
                  value={lead.Move_In_Date ? formatDate(lead.Move_In_Date) : null}
                />
                <DetailItem label="Budget range" value={budgetRange(lead)} />
              </div>
            </section>

            <section className="cportal-lead-crm-card">
              <h3>Lead Pipeline</h3>
              <div className="cportal-lead-crm-grid">
                <DetailItem label="Status" value={lead.Lead_Status} />
                <DetailItem label="Source" value={lead.Source_Name} />
                <DetailItem label="Site visit" value={lead.Site_Visit} />
                <DetailItem label="Opportunity" value={lead.Is_Opportunity} />
                <DetailItem label="Automation" value={lead.Automation_Enabled} />
              </div>
            </section>

            <section className="cportal-lead-crm-card cportal-lead-crm-card--wide">
              <h3>Notes &amp; Audit</h3>
              <div className="cportal-lead-crm-grid cportal-lead-crm-grid--wide">
                <DetailItem label="Notes" value={lead.Notes} />
                <DetailItem label="Created on">
                  <span className="cportal-datetime-value">
                    <FaCalendarAlt aria-hidden="true" />
                    {formatDateTime(lead.Created_On)}
                  </span>
                </DetailItem>
                <DetailItem label="Updated on">
                  <span className="cportal-datetime-value">
                    <FaCalendarAlt aria-hidden="true" />
                    {formatDateTime(lead.Updated_On)}
                  </span>
                </DetailItem>
              </div>
            </section>
          </div>
        ) : (
          <section className="cportal-lead-crm-activity" role="tabpanel">
            <div className="cportal-lead-crm-activity-head">
              <div>
                <h3>Follow-ups</h3>
              </div>
              <button
                type="button"
                className="cportal-btn cportal-btn-primary"
                onClick={openAddFollowupModal}
              >
                Add follow-up
              </button>
            </div>

            {followups.length === 0 ? (
              <p className="cportal-muted">No follow-ups recorded yet.</p>
            ) : (
              <>
              <div className="cportal-table-wrap co-desktop-table">
                <table className="cportal-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Remarks</th>
                      <th>Next follow-up</th>
                      <th style={{ width: 72 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {followups.map((f) => (
                      <tr key={f.Followup_Id}>
                        <td>{formatDate(f.Followup_Date)}</td>
                        <td>{f.Followup_Type || "—"}</td>
                        <td className="cportal-td-wrap">{f.Remarks || "—"}</td>
                        <td>{formatDate(f.Next_Followup_Date)}</td>
                        <td>
                          <button
                            type="button"
                            className="re-lead-action re-lead-action--edit"
                            title="Edit follow-up"
                            aria-label="Edit follow-up"
                            onClick={() => openEditFollowupModal(f)}
                          >
                            <FaEdit size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ResultsMobileList loading={false} emptyLabel="No follow-ups recorded yet.">
                {followups.map((f) => (
                  <ResultsMobileCard
                    key={f.Followup_Id}
                    title={formatDate(f.Followup_Date)}
                    meta={f.Followup_Type ? [{ label: f.Followup_Type, tone: "info" }] : []}
                    fields={[
                      { label: "Next follow-up", value: formatDate(f.Next_Followup_Date) },
                      { label: "Remarks", value: f.Remarks || "—" },
                    ]}
                    onOpen={() => openEditFollowupModal(f)}
                    actions={
                      <div className="co-mcard__action-grid">
                        <button type="button" className="co-maction" onClick={() => openEditFollowupModal(f)}>
                          <FaEdit size={13} />
                          <span>Edit</span>
                        </button>
                      </div>
                    }
                  />
                ))}
              </ResultsMobileList>
              </>
            )}
          </section>
        )}
      </div>

      {followupModalOpen ? (
        <PortalLeadFollowupModal
          leadId={id}
          leadName={fullName(lead)}
          followup={editingFollowup}
          onClose={closeFollowupModal}
          onSaved={async () => {
            await reloadLead();
            setMessage({
              type: "success",
              text: editingFollowup ? "Follow-up updated." : "Follow-up added.",
            });
          }}
        />
      ) : null}

      <PortalConfirmModal
        open={confirmDelete}
        title="Delete Lead"
        message={
          lead ? (
            <p>
              Delete <strong>{fullName(lead)}</strong>? The lead will be removed from your list.
            </p>
          ) : null
        }
        confirming={deleting}
        confirmingLabel="Deleting…"
        onConfirm={confirmDeleteLead}
        onClose={closeDeleteConfirm}
      />
    </div>
  );
}

export default TenantLeadView;
